// Façade du moteur v2 pour l'app et le rejeu serveur (lot V3).
//
// Une feuille publiée en `track = 'packs'` garde le modèle du noyau tel quel (`model.kernel`, symboles, pièges,
// outils) plus son niveau (`model.level`) : l'app et le serveur l'ouvrent ici avec le MÊME moteur (tous les packs
// du dépôt, compilés une fois), et jugent le même texte de ligne (celui que l'app enregistre, `line_text`).
//
// Pur : aucun réseau. Importé par l'app (chargé à la demande) et empaqueté pour l'edge function `scratch-verify`
// (scripts/bundle-deno.mjs).
import { compile } from './compiler/compile.js';
import { PACKS } from './packs.js';
import {
  createSheet,
  judge,
  commit,
  hint,
  progress,
  sheetClaimDecisions,
  sheetStatedValues,
  sheetHasAnswer,
} from './kernel/judge.js';
import { instantiate } from './kernel/params.js';
import { parse } from './kernel/syntax.js';
import { equivExpr, equivRelation, isRelation } from './kernel/equiv.js';
import { isLeaf } from './kernel/ast.js';
import { advLayout, advSolution } from './kernel/progress-sheets.js';
import {
  blockModels,
  givenBlock,
  givenBranches,
  judgeBlock,
  okCellCount,
  parseBlockLine,
} from './blocks.js';

/**
 * Version d'une feuille `packs` (`scratch_models.engine_version`). Au-dessus de toute version de l'ancien moteur
 * (gelé à 64) : une app qui n'a que l'ancien moteur ne la joue jamais (elle joue la version `kernel`).
 */
export const PACKS_ENGINE_VERSION = 100;

/** Niveaux de l'app → niveaux connus des packs (la prépa joue au niveau Terminale). */
const LEVEL = { tle: 'Tle', mpsi: 'Tle', ptsi: 'Tle', mp: 'Tle', pt: 'Tle' };
export const packsLevel = (level) => (level ? (LEVEL[level] ?? level) : undefined);

let cached = null;
/** Le moteur compilé (tous les packs du dépôt), une seule fois par processus. */
export function packsEngine() {
  if (cached) return cached;
  const { engine, diagnostics } = compile(
    PACKS,
    PACKS.map((p) => p.id),
  );
  const errors = diagnostics.filter((d) => d.level === 'error');
  if (errors.length) throw new Error(`packs invalides : ${errors.map((d) => d.msg).join(' ; ')}`);
  cached = engine;
  return engine;
}

/** Empreinte du moteur (packs + compilateur) : enregistrée avec une feuille convertie. */
export const packsEngineHash = () => packsEngine().meta.hash;

// Blocs de la feuille (arbre pondéré, outils d'appoint) : leurs modèles, rangés par feuille ouverte (`ctx` est
// partagé par tous les états d'une même feuille).
const BLOCKS = new WeakMap();
const blocksOf = (sheet) => (sheet?.ctx ? BLOCKS.get(sheet.ctx) : null) ?? { main: null, aux: {} };

/** Ouvre la feuille d'un modèle publié. */
export function openPacksSheet(model) {
  const engine = packsEngine();
  const exercise = model?.params ? instantiate({ model }) : { model };
  const sheet = createSheet(engine, exercise, { level: packsLevel(model?.level) });
  if (sheet?.ctx && model?.blocks) BLOCKS.set(sheet.ctx, blockModels(engine, exercise.model));
  return sheet;
}

/**
 * Les arbres pondérés d'une feuille (`model.blocks.tree`) : { nom: { levels, words, numbers, paths, given } } ;
 * `given` = les branches fournies par l'énoncé (à compléter), sinon null (l'élève construit l'arbre).
 */
export function packsTrees(sheet) {
  const out = {};
  for (const [name, b] of Object.entries(blocksOf(sheet).aux)) {
    if (b.type !== 'tree' || b.spec?.tool) continue;
    let given = null;
    try {
      const tpl = givenBlock('tree', b.M);
      given = tpl ? givenBranches(tpl) : null;
    } catch {
      given = null;
    }
    out[name] = {
      levels: b.M.levels,
      words: b.M.words ?? [],
      numbers: [...(b.M.numbers ?? [])],
      paths: !!b.M.paths,
      frac: !!b.M.frac,
      given,
    };
  }
  return out;
}

export { treeLineText, treeCellId } from './blocks.js';

/** Feuille « tableau d'avancement » (plugin avancement, lot P2) : espèces, unité, et le tableau juste (Corrigé). */
export function packsAdvancement(sheet) {
  const ks = sheet?.ctx?.adv;
  if (!ks) return null;
  return { ...advLayout(ks), solution: advSolution(ks) };
}
export { advText as advancementLineText } from './kernel/progress-sheets.js';

/**
 * Une ligne « bloc » (« ⑂{…} » arbre, « ▦{…} » tableau) : jugée case par case par le noyau. Juste et complète :
 * un pas (1 HORA par case juste) ; sinon fausse, avec l'état de chaque case (`cells`) et la première remarque.
 */
function judgeBlockText(sheet, text) {
  const parsed = parseBlockLine(text);
  const entry = parsed?.aux ? blocksOf(sheet).aux[parsed.aux] : null;
  if (!parsed || !entry || entry.type !== parsed.type)
    return {
      status: 'parse_error',
      kind: null,
      text,
      gestures: [],
      hora: 0,
      message: 'Cet outil n’existe pas dans cet exercice.',
    };
  const judged = judgeBlock(parsed.type, entry.M, parsed.view);
  const label = parsed.type === 'tree' ? 'Arbre pondéré' : 'Tableau';
  const block = { name: parsed.aux, type: parsed.type, done: !!judged.done, cells: judged.cells };
  if (judged.done)
    return {
      status: 'step',
      kind: 'step',
      text,
      title: parsed.type === 'tree' ? 'Arbre complet' : 'Tableau complet',
      gestures: [{ label, ruleId: `block.${parsed.type}` }],
      hora: okCellCount(judged, parsed.type),
      block,
    };
  const bad = Object.values(judged.cells).find((c) =>
    ['wrong', 'trap', 'unfinished'].includes(c.status),
  );
  const note = judged.notes?.[0]?.message;
  return {
    status: 'wrong',
    kind: null,
    text,
    gestures: [],
    hora: 0,
    title:
      bad?.title ??
      (parsed.type === 'tree' ? 'L’arbre n’est pas fini' : 'Le tableau n’est pas fini'),
    message: bad?.message || note || 'Complète les cases vides.',
    block,
  };
}

export function judgePacksLine(sheet, text) {
  const t = String(text);
  if (parseBlockLine(t)) return judgeBlockText(sheet, t);
  return judge(packsEngine(), sheet, t);
}
/** Un bloc juste ne s'écrit pas parmi les lignes du moteur : la feuille retient seulement qu'il est fait. */
export function commitPacksLine(sheet, verdict) {
  if (verdict?.block)
    return verdict.block.done
      ? { ...sheet, blocksDone: { ...(sheet.blocksDone ?? {}), [verdict.block.name]: true } }
      : sheet;
  return commit(sheet, verdict);
}
export const packsHint = (sheet) => hint(packsEngine(), sheet);
export const packsProgress = (sheet) => progress(packsEngine(), sheet);
export const packsClaimDecisions = sheetClaimDecisions;
export const packsStatedValues = sheetStatedValues;
export const packsHasAnswer = (sheet, text) => sheetHasAnswer(packsEngine(), sheet, text);

/** La feuille est-elle finie (la réponse est écrite) ? */
export function packsDone(sheet) {
  return !!sheet.done || !!progress(packsEngine(), sheet).done;
}

/**
 * Genre d'une ligne enregistrée à partir du verdict : `step` (juste, justifiée, utile, HORA), `kept` (juste, gardée
 * sans HORA : inutile, ou juste mais non justifiée), sinon null (la ligne ne s'écrit pas).
 */
export function packsLineKind(v) {
  if (!v) return null;
  if (v.status === 'step' || v.status === 'goal') return 'step';
  if (v.status === 'useless' || (v.status === 'unjustified' && v.kind === 'kept')) return 'kept';
  return null;
}

/**
 * Le BONUS AU GESTE, borne haute (clavier v3). L'élève qui simplifie morceau par morceau dans l'éditeur
 * (`3²` → `9`, enlever des parenthèses, annuler une paire) touche +1 par geste, versé à la validation en
 * plus des HORA du pas. Le client les compte ; le serveur ne le croit pas sur parole : il recalcule ici
 * combien de simplifications séparent VRAIMENT la ligne d'avant de celle qu'on valide, et n'accorde que
 * `min(déclaré, ce majorant)`.
 *
 * Le poids d'une écriture = ses opérations (les nœuds qui ne sont pas des feuilles, la relation mise à
 * part) plus ses parenthèses. Simplifier retire des opérations ; développer ou multiplier en haut et en
 * bas en ajoute, donc ne paie rien (majorant 0) — conforme à la spec. Un texte illisible rend 0.
 */
export const MAX_SIMPLIFY_BONUS = 4;

const parenCount = (text) => text.match(/\(/g)?.length ?? 0;

function treeWeight(n, table) {
  let ops = 0;
  const go = (u, root) => {
    if (!u || typeof u !== 'object' || !u.t) return;
    // un « − » devant un nombre ou une lettre est un SIGNE, pas un calcul à faire : « 26 − 10 » pèse
    // autant qu'une addition, sinon le majorant doublerait sur toute soustraction
    const sign = u.t === 'Neg' && u.k?.length === 1 && isLeaf(u.k[0]);
    if (!isLeaf(u) && !sign && !(root && isRelation(u, table))) ops++;
    for (const kid of u.k ?? []) go(kid, false);
  };
  go(n, true);
  return ops;
}

export function packsSimplifyBonus(sheet, prevText, lineText) {
  const ctx = sheet?.ctx;
  const table = ctx?.table;
  if (!table || !prevText || !lineText) return 0;
  try {
    const a = parse(prevText, table);
    const b = parse(lineText, table);
    const d =
      treeWeight(a, table) - treeWeight(b, table) + parenCount(prevText) - parenCount(lineText);
    if (d <= 0) return 0;
    // « raccourcir À VALEUR ÉGALE » : effacer un morceau raccourcit aussi, mais ce n'est pas un geste
    const same =
      isRelation(a, table) && isRelation(b, table)
        ? equivRelation(a, b, ctx)
        : equivExpr(a, b, table, { hyps: ctx.hyps ?? [], fixed: ctx.fixed ?? {} });
    return same ? Math.min(MAX_SIMPLIFY_BONUS, d) : 0;
  } catch {
    return 0;
  }
}

/**
 * Rejeu d'une feuille `packs` enregistrée (piste S) : chaque ligne (son texte) est rejugée dans l'ordre ; une ligne
 * qui ne se rejuge pas comme elle a été enregistrée n'apprend rien et ne rapporte rien. `gain` = l'économie de la
 * feuille (`scratchLineGain` de l'ancien moteur : une seule source pour l'app et le serveur).
 *
 * rows : { position, text, kind: 'step' | 'kept', hora, hinted, simplified? }[]
 */
export function replayPacksSheet({ model, maxHora, rows }, gain) {
  const sorted = [...rows].sort((a, b) => a.position - b.position);
  const claimed = sorted.reduce((s, r) => s + Math.max(0, r.hora), 0);
  let sheet;
  try {
    // une feuille `packs` porte le modèle du noyau (contrainte de la base, migration 0693)
    if (!model || typeof model !== 'object' || !model.kernel) throw new Error('model_unreadable');
    sheet = openPacksSheet(model);
  } catch {
    return {
      rows: sorted.map((r) => ({
        position: r.position,
        status: 'unchecked',
        hora: Math.max(0, r.hora),
        bound: Math.max(0, r.hora),
        verdict: 'unchecked',
        ruleId: null,
      })),
      hora: claimed,
      claimed,
      engine: PACKS_ENGINE_VERSION,
      reason: 'model_unreadable',
    };
  }
  let used = 0;
  const out = [];
  // la ligne d'où part la suivante : la dernière gardée, sinon le départ de la feuille (pour le bonus au geste)
  let prevText = typeof model.kernel.start === 'string' ? model.kernel.start : '';
  for (const r of sorted) {
    let v = null;
    try {
      v = r.text ? judgePacksLine(sheet, r.text) : null;
    } catch {
      v = null;
    }
    const kind = packsLineKind(v);
    if (!v || kind !== r.kind) {
      out.push({
        position: r.position,
        status: 'rejected',
        hora: 0,
        bound: 0,
        verdict: v?.status ?? 'error',
        ruleId: null,
      });
      continue;
    }
    // bonus au geste : ce que l'app a déclaré, borné par ce que le moteur voit de simplifiable
    const bonus =
      kind === 'step' && r.simplified
        ? Math.min(Math.max(0, r.simplified), packsSimplifyBonus(sheet, prevText, r.text))
        : 0;
    const bound =
      kind === 'step'
        ? gain(v.hora ?? 0, { hinted: r.hinted, maxHora: maxHora ?? null, used, bonus })
        : 0;
    const hora = Math.min(Math.max(0, r.hora), bound);
    used += hora;
    prevText = r.text;
    sheet = commitPacksLine(sheet, v);
    out.push({
      position: r.position,
      status: 'verified',
      hora,
      bound,
      verdict: v.status,
      ruleId: v.gestures?.[0]?.ruleId ?? null,
    });
  }
  return {
    rows: out,
    hora: out.reduce((s, r) => s + r.hora, 0),
    claimed,
    engine: PACKS_ENGINE_VERSION,
  };
}
